import { LEVEL, LEVEL_PROPERTIES } from '@/interfaces/level.interface'
import { orderOnLevel } from '../utils/sort'
import { getRequestGateway } from '@/services/api.service'

export async function getFloorPlansForLevelV1(id: string): Promise<any> {
  return getRequestGateway('core/v1/levels/' + id + '/floorplans')
}

export async function getLevelv1(id: string): Promise<any> {
  return getRequestGateway('core/v1/levels/' + id)
}

export async function getLevelsv1(id: string): Promise<LEVEL_PROPERTIES[]> {
  return (await getRequestGateway('core/v1/levels?constructionId=' + id)).data as LEVEL_PROPERTIES[]
}

async function getLevelsGeoV1(id: string): Promise<LEVEL[]> {
  return (await getRequestGateway('core/v1/levels/geojson?constructionId=' + id)).data as LEVEL[]
}

function joinLevelData(
  geojson: LEVEL[],
  levelProperties: LEVEL_PROPERTIES[],
  buildingGeometry: GeoJSON.Polygon | GeoJSON.MultiPolygon
): LEVEL[] {
  if (geojson.length === 0) {
    return levelProperties.map((level) => ({
      id: level.id,
      geometry: buildingGeometry,
      properties: level,
      type: 'Feature' as const
    }))
  }
  return geojson.map((level) => {
    const feature = levelProperties.find((feature) => feature.id === level.properties.belongsToId)
    if (feature) {
      level.properties = feature
      level.id = feature.id
      level.properties.belongsToId = feature.id
    }
    return level
  })
}

function updateHeightAndElevation(levels: LEVEL[]): LEVEL[] {
  let additionalHeight = 0
  const gapHeight = 0.5 // Height of the gap between levels
  let currentElevation = 0 // Tracks the elevation for when the elevation property is missing for a level
  let minElevation: number

  return levels.map((level, counter) => {
    if (counter === 0) {
      minElevation = level.properties.elevation ?? 0
      level.properties.elevation -= minElevation
    } else if (!level.properties.elevation) {
      level.properties.elevation = currentElevation
    } else {
      level.properties.elevation -= minElevation!
      level.properties.elevation += gapHeight + additionalHeight
    }
    if (!level.properties.height) {
      level.properties.height = 3
    }
    if (level.properties.height < 3) {
      const newHeight = level.properties.elevation + 3
      additionalHeight += newHeight - level.properties.height
      level.properties.height = newHeight
    } else {
      level.properties.height = level.properties.elevation - gapHeight + level.properties.height
    }
    currentElevation = level.properties.height + gapHeight
    return level
  })
}

export async function getLevelsForBuilding(
  buildingId: string,
  buildingGeometry: GeoJSON.Polygon | GeoJSON.MultiPolygon,
  getFloorPlans: boolean = true,
  bypassGeometry: boolean = false
): Promise<any> {
  try {
    const geojson = await getLevelsGeoV1(buildingId)
    if (!geojson && !bypassGeometry) return null
    const levelProperties = await getLevelsv1(buildingId)
    let levels = joinLevelData(geojson, levelProperties, buildingGeometry)
    levels.sort(orderOnLevel)

    if (getFloorPlans) {
      levels = await Promise.all(
        levels.map((level) =>
          getFloorPlansForLevelV1(level.id).then(
            (floorplans: any) =>
              (level.properties.hasFloorPlans = floorplans ? floorplans.data : [])
          )
        )
      )
    }

    levels = updateHeightAndElevation(levels)

    return {
      type: 'geojson',
      data: {
        id: 'sourceLevel',
        type: 'FeatureCollection',
        features: levels
      }
    }
  } catch (e: any) {
    console.error(e)
    throw new Error('Could not fetch levels')
  }
}
